import { EmptyStateCard } from '@/components/ui/states/EmptyStateCard';
import { Card, CardBody, CardHeader } from '@/components/ui/card/Card';
import type { BrdSnapshotSection, ProjectArtifact } from '@/api/project-artifacts.types';

type Props = {
  artifact: ProjectArtifact;
};

function sectionsInSortOrder(artifact: ProjectArtifact): BrdSnapshotSection[] {
  const snapshot = artifact.contentSnapshot;
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return [];
  }
  const sections = (snapshot as { sections?: unknown }).sections;
  if (!Array.isArray(sections)) return [];
  return [...sections]
    .filter((s): s is BrdSnapshotSection => {
      return (
        !!s &&
        typeof s === 'object' &&
        typeof s.key === 'string' &&
        typeof s.name === 'string' &&
        typeof s.sortOrder === 'number'
      );
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Phase 1 BRD surface: render the persisted snapshot only.
 * Labels come from the artifact response. This component must not fetch a
 * template definition — the snapshot is the document.
 */
export function BrdReadOnlyView({ artifact }: Props): JSX.Element {
  const sections = sectionsInSortOrder(artifact);

  if (sections.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-6" data-testid="brd-snapshot-missing">
        <EmptyStateCard
          title="This document is unavailable"
          description="The saved snapshot for this document could not be loaded. It was not rebuilt from a live template."
        />
      </div>
    );
  }

  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8"
      data-testid="brd-readonly-view"
    >
      <header className="mb-6 space-y-1">
        <h2 className="text-xl font-semibold text-slate-900">{artifact.name}</h2>
        {artifact.templateLabel ? (
          <p className="text-sm text-slate-600" data-testid="brd-template-label">
            {artifact.templateLabel}
            {artifact.templateVersionLabel ? (
              <span data-testid="brd-template-version-label">
                {' '}
                · {artifact.templateVersionLabel}
              </span>
            ) : null}
          </p>
        ) : null}
      </header>

      <ol className="space-y-4" data-testid="brd-sections">
        {sections.map((section) => (
          <li key={section.key} data-testid={`brd-section-${section.key}`} data-sort-order={section.sortOrder}>
            <Card>
              <CardHeader className="p-4 pb-2">
                <h3 className="text-sm font-semibold text-slate-900">{section.name}</h3>
              </CardHeader>
              <CardBody className="p-4 pt-0">
                {section.content.trim() ? (
                  <p className="whitespace-pre-wrap text-sm text-slate-700">{section.content}</p>
                ) : (
                  <p className="text-sm text-slate-400">Not yet filled</p>
                )}
              </CardBody>
            </Card>
          </li>
        ))}
      </ol>
    </div>
  );
}
